@switch (type) {
  @case ('list-item') {
    <div class="list-item">
      <div class="details">
        <div class="icon-name">
          <rt-icon name="attach-file" margin-right-small-xx colorToken="colorIconLowDefault" />
          @if (isDocumentOrImage) {
            <a class="link" (click)="open()">{{ attachment.filename }}</a>
          }
          @if (!isDocumentOrImage) {
            <a class="link" [href]="attachment.fileUrl" (click)="onAnchorClick($event)">{{
              attachment.filename
            }}</a>
          }
          @if (attachment.fileSize) {
            <span text-light class="size">({{ attachment.fileSize | rtFileSize }})</span>
          }
        </div>
        @if (hasDeleteAction) {
          <rt-icon-button
            variant="plain"
            (click)="delete()"
            size="small"
            class="danger-on-hover"
            shape="square"
            iconName="delete"
          />
        }
      </div>
      <rt-divider variant="line dark" margin-top-small />
    </div>
  }

  @case ('card') {
    <div
      class="rt-card"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="preview" />
      <ng-container *ngTemplateOutlet="footer" />
    </div>
  }

  @case ('compact-card') {
    <div
      class="rt-card rt-card-background-light attachment-small"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="preview" />
      <ng-container *ngTemplateOutlet="footer" />
    </div>
  }

  @case ('tile') {
    <div
      class="rt-card rt-card-background-light attachment-small-x"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
    >
      <ng-container *ngTemplateOutlet="footer" />
    </div>
  }

  @case ('preview') {
    <div
      class="rt-card rt-card-background-light attachment-small"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
      [class.yellow]="yellowBorder"
    >
      <ng-container *ngTemplateOutlet="preview" />
    </div>
  }
  @case ('preview-large') {
    <div
      class="rt-card attachment-large"
      [class.additional-opts-visible]="additionalOptsDropdownOpen"
      [class.private]="isPrivate"
      [class.yellow]="yellowBorder"
    >
      <ng-container *ngTemplateOutlet="preview" />
    </div>
  }
}

<ng-template #preview>
  <div class="preview">
    @if (imageLoading) {
      <rt-spinner />
    }
    @if (!imageLoading) {
      @if (hasThumbnail) {
        <img [src]="thumbnailUrl" (error)="thumbnailLoadError()" />
      }
      @if (!hasThumbnail && emptyStateTemplate) {
        <ng-container [ngTemplateOutlet]="emptyStateTemplate" />
      }
      @if (!hasThumbnail && !emptyStateTemplate) {
        <div class="extension">
          <div class="extension__oval">
            <div text-uppercase text-weight-semi-bold class="extension__name">
              {{ attachment.extension }}
            </div>
          </div>
        </div>
      }
    }
    <div class="overlay">
      <div class="overlay__inner">
        @if (actions.length) {
          <div class="actions">
            @if (actions.length === 1) {
              <rt-icon-button
                [ngClass]="actions[0].variant || {}"
                [rtTooltip]="actions[0].label | translate"
                (click)="actions[0].handler()"
                variant="plain-onoverlay"
                shape="square"
                [size]="type === 'compact-card' ? 'small' : 'medium'"
                [iconName]="actions[0].icon"
                class="delete-button"
              />
            } @else {
              <rt-icon-button
                size="small"
                shape="square"
                iconName="more-horiz"
                variant="plain-onoverlay"
                #additionalOptsTrigger="rtOverlayOrigin"
                rt-overlay-origin
                (click)="toggleAdditionalOptsDropdown(true, $event)"
                [pressed]="additionalOptsDropdownOpen"
                data-cy="candidate-profile:candidate-profile-attachment:more-button"
              />

              <ng-template
                #additionalOpts="rtConnectedOverlay"
                rt-connected-overlay
                [hasBackdrop]="true"
                [offsetX]="-5"
                [origin]="additionalOptsTrigger"
                [open]="additionalOptsDropdownOpen"
                (backdropClick)="toggleAdditionalOptsDropdown(false)"
              >
                <rt-dropdown (click)="$event.preventDefault()">
                  @for (action of actions; track trackByLabel($index, action)) {
                    <rt-dropdown-item (click)="action.handler()">
                      <rt-icon prefix [name]="action.icon" />
                      {{ action.label | translate }}
                    </rt-dropdown-item>
                  }
                </rt-dropdown>
              </ng-template>
            }
          </div>
        }

        @if (isDocumentOrImage) {
          <div (click)="open()" class="open-preview-layer">
            <rt-icon name="visibility" size="medium" colorToken="colorIconOnoverlayDefault" />
          </div>
        }

        @if (!isDocumentOrImage && hasDownloadAction) {
          <a
            rt-button
            [class.small]="type === 'compact-card'"
            [href]="href"
            download
            (click)="onAnchorClick($event)"
            variant="primary"
          >
            <rt-icon name="file-download" size="medium" />
            <span>{{ translations.previewDialog.downloadFile | translate }}</span>
          </a>
        }
      </div>
    </div>
  </div>
</ng-template>

<ng-template #footer>
  <div class="footer">
    @if (!hasDownloadAction) {
      <span text-bold>{{ attachment.filename }}</span>
    }
    @if (hasDownloadAction) {
      <div class="details">
        @if (type === 'tile') {
          <rt-icon name="attach-file" />
        }
        @if (isPrivate) {
          <rt-icon
            [rtTooltip]="'rtu.attachment.private_file_tooltip' | translate"
            [rtTooltipMaxWidth]="200"
            name="lock"
          />
        }
        <rt-truncate>
          @if (attachment.filename) {
            @if (!highlightedFilename && isDocumentOrImage) {
              <div class="name" (click)="open()">
                {{ attachment.filename }}
              </div>
            }
            @if (highlightedFilename && isDocumentOrImage) {
              <div
                [innerHtml]="highlightedFilename"
                (click)="open()"
                class="name name--highlighted"
              ></div>
            }
            @if (!highlightedFilename && !isDocumentOrImage) {
              <a
                href="{{ attachment.fileUrl }}"
                class="name"
                (click)="onAnchorClick($event)"
                download
              >
                {{ attachment.filename }}
              </a>
            }
            @if (highlightedFilename && !isDocumentOrImage) {
              <a
                href="{{ attachment.fileUrl }}"
                [innerHtml]="highlightedFilename"
                class="name name--highlighted"
                (click)="onAnchorClick($event)"
                download
              >
              </a>
            }
          }
        </rt-truncate>

        @if (footerIcon || moreTemplate) {
          <div layout-horizontal>
            @if (footerIcon) {
              <div class="footer__icon-wrap">
                <rt-icon [name]="footerIcon" />
              </div>
            }
            @if (moreTemplate) {
              <div>
                <rt-icon-button
                  size="small"
                  iconName="more-horiz"
                  shape="square"
                  #moreDropdownOrigin="rtOverlayOrigin"
                  [pressed]="moreDropdown.open"
                  rt-overlay-origin
                  variant="plain"
                  (click)="moreDropdown.open = !moreDropdown.open"
                />

                <ng-template
                  #moreDropdown="rtConnectedOverlay"
                  rt-connected-overlay
                  [hasBackdrop]="true"
                  [origin]="moreDropdownOrigin"
                  (backdropClick)="moreDropdown.open = false"
                >
                  <ng-template
                    [ngTemplateOutlet]="moreTemplate"
                    [ngTemplateOutletContext]="{ attachmentAssignment: attachmentAssignment }"
                  />
                </ng-template>
              </div>
            }
          </div>
        }
      </div>
    }
    @if (hasUploader && type === 'card') {
      <div class="user">
        <rt-avatar
          [type]="avatarData.type"
          [size]="'small'"
          [shape]="'oval'"
          [imageSrc]="avatarData.photoThumbUrl"
          [initials]="avatarData.initials"
          [context]="avatarData.context"
          [iconName]="avatarData.iconName!"
          [customIconBackgroundColor]="avatarData.customIconBackgroundColor"
          [customIconColor]="avatarData.customIconColor"
        />
        <div layout-vertical>
          <div layout>
            <div class="user-name" text-weight-semi-bold>{{ uploaderName }}</div>
            @if (attachment.guestId) {
              <rt-badge
                color="optional"
                [text]="'app.common.guest' | translate"
                margin-left-small-x
              />
            }
          </div>
          <rt-date [time]="attachment.createdAt" />
        </div>
      </div>
    }
  </div>
</ng-template>
